import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/css/Styles1.css';
import './DashboardSelector.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import SEO from '../components/SEO';

function DashboardSelector({ onLogout }) {
  const navigate = useNavigate();
  const [cobertura, setCobertura] = useState(null);
  const [coberturaLoading, setCoberturaLoading] = useState(true);
  const [coberturaError, setCoberturaError] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState('INCOMPLETAS');
  const [busqueda, setBusqueda] = useState('');
  const [listaAbierta, setListaAbierta] = useState(false);

  useEffect(() => {
    const cargarCobertura = async () => {
      try {
        setCoberturaLoading(true);
        setCoberturaError('');
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost/Evaluacion/backend';
        const resp = await fetch(`${apiBase}/api/admin/evaluaciones/cobertura`);
        if (!resp.ok) throw new Error('No se pudo cargar el seguimiento');
        const json = await resp.json();
        if (!json.success) throw new Error(json.message || 'Error al cargar cobertura');
        setCobertura(json);
      } catch (e) {
        setCoberturaError(e.message || 'Error al cargar el seguimiento de evaluaciones');
      } finally {
        setCoberturaLoading(false);
      }
    };
    cargarCobertura();
  }, []);

  const itemsFiltrados = useMemo(() => {
    if (!cobertura?.data) return [];
    const q = busqueda.trim().toLowerCase();
    return cobertura.data.filter((item) => {
      if (filtroEtapa === 'INCOMPLETAS' && item.completa) return false;
      if (filtroEtapa === 'COMPLETAS' && !item.completa) return false;
      if (filtroEtapa === 'HSEQ_PENDIENTE' && item.tiene_hseq) return false;
      if (filtroEtapa === 'HSEQ_COMPLETA' && !item.tiene_hseq) return false;
      if (
        filtroEtapa !== 'TODAS' &&
        filtroEtapa !== 'INCOMPLETAS' &&
        filtroEtapa !== 'COMPLETAS' &&
        filtroEtapa !== 'HSEQ_PENDIENTE' &&
        filtroEtapa !== 'HSEQ_COMPLETA'
      ) {
        if (item.etapa !== filtroEtapa) return false;
      }
      if (!q) return true;
      return (
        String(item.nombre || '').toLowerCase().includes(q) ||
        String(item.cedula || '').includes(q) ||
        String(item.cargo || '').toLowerCase().includes(q) ||
        String(item.area || '').toLowerCase().includes(q) ||
        String(item.jefe_nombre || '').toLowerCase().includes(q)
      );
    });
  }, [cobertura, filtroEtapa, busqueda]);

  const pasoClase = (ok) => (ok ? 'paso paso-ok' : 'paso paso-pend');

  const pasosDeItem = (item) => ({
    auto: Boolean(item.auto_hecha) || ['PENDIENTE_JEFE', 'PENDIENTE_HSEQ', 'COMPLETA'].includes(item.etapa),
    jefe: Boolean(item.jefe_hecho) || ['PENDIENTE_HSEQ', 'COMPLETA'].includes(item.etapa),
    hseq: Boolean(item.tiene_hseq) || item.etapa === 'COMPLETA',
  });

  // Funciones para manejar los clicks en los botones
  const handleEmpleadosClick = () => {
    navigate('/admin/empleados');
  };

  // Gestión de Funciones eliminada

  const handleCargosClick = () => {
    navigate('/admin/cargos');
  };

  const handleCompetenciasClick = () => {
    navigate('/admin/competencias');
  };

  const handlePeriodosClick = () => {
    navigate('/admin/periodos');
  };

  // PDF individual por empleado (reutiliza los endpoints de Results)
  const descargarPdfDesempeno = (item) => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost/Evaluacion/backend';
    window.open(`${apiBase}/api/evaluations/${item.id_evaluacion}/pdf/${item.id_empleado}`, '_blank');
  };

  const descargarPdfHseq = (item) => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost/Evaluacion/backend';
    const periodo = String(item.hseq_periodo || '').trim();
    const sufijo = /^\d{4}-\d{2}$/.test(periodo) ? `/${periodo}` : '';
    window.open(`${apiBase}/api/hseq/employee/${item.id_empleado}/pdf${sufijo}`, '_blank');
  };

  const handleDownloadReportClick = () => {
    generateAllEvaluationsExcel();
  };

  const generateAllEvaluationsExcel = async () => {
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost/Evaluacion/backend';
      const resp = await fetch(`${apiBase}/api/evaluations/all-with-details`);
      if (!resp.ok) throw new Error('Error al obtener evaluaciones');
      const json = await resp.json();
      const rows = Array.isArray(json.data) ? json.data : [];

      const wb = new ExcelJS.Workbook();
      wb.created = new Date();
      wb.properties.title = 'Reporte Evaluaciones';
      wb.properties.company = 'Meridian Consulting LTDA';

      // Hoja principal con resumen
      const ws = wb.addWorksheet('Resumen Evaluaciones', { views: [{ state: 'frozen', ySplit: 6 }] });
      
      // Hoja para competencias detalladas
      const wsCompetencias = wb.addWorksheet('Competencias Detalladas', { views: [{ state: 'frozen', ySplit: 1 }] });
      
      // Hoja para HSEQ detallado
      const wsHseq = wb.addWorksheet('HSEQ Detallado', { views: [{ state: 'frozen', ySplit: 1 }] });
      
      // Hoja para Compromisos y Seguimiento
      const wsCompromisos = wb.addWorksheet('Compromisos y Seguimiento', { views: [{ state: 'frozen', ySplit: 1 }] });

      // Título
      ws.mergeCells('A1:AA1');
      const title = ws.getCell('A1');
      title.value = 'REPORTE DE EVALUACIONES - MERIDIAN CONSULTING LTDA';
      title.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF1F3B73' } };
      title.alignment = { horizontal: 'center', vertical: 'middle' };

      ws.getCell('A3').value = 'Fecha de generación:';
      ws.getCell('B3').value = new Date().toLocaleString('es-CO');
      ws.getCell('A4').value = 'Total de evaluaciones:';
      ws.getCell('B4').value = rows.length;

      // Encabezados principales
      const headers = [
        'ID Evaluación','ID Empleado','Cédula','Nombre','Cargo','Área','Email','Fecha Evaluación','Período',
        'Estado','Fecha Creación','Fecha Autoevaluación','Fecha Eval. Jefe','Fecha Eval. HSEQ',
        'Nombre Jefe','Cargo Jefe','Evaluador HSEQ','Cargo Eval. HSEQ','Obs. Generales','Comentarios HSEQ',
        'Promedio Autoevaluación','Promedio Eval. Jefe','Promedio HSEQ','Promedio Competencias',
        '% Meridian','Score Meridian (1-5)','Promedio General'
      ];
      ws.addRow([]); // fila 5 vacía
      const headerRow = ws.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.font = { name: 'Calibri', bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = { top:{style:'thin'}, left:{style:'thin'}, right:{style:'thin'}, bottom:{style:'thin'} };
      });

      const formatDate = (v) => {
        if (!v) return '';
        const d = new Date(v);
        return isNaN(d) ? ('' + v) : d.toLocaleString('es-CO');
      };

      // Componente ausente (null) se deja vacío: nunca se muestra como 0
      const valorONada = (v) => {
        if (v === null || v === undefined || v === '') return '';
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : '';
      };

      // Datos principales — el API entrega el resultado ya calculado por modelo y período
      rows.forEach((r, idx) => {
        const promedioGeneral = r.estado_calculo === 'SIN_DATOS'
          ? 'Sin datos suficientes'
          : valorONada(r.promedio_general);

        const row = ws.addRow([
          r.id_evaluacion,
          r.id_empleado,
          r.empleado_cedula,
          r.empleado_nombre,
          r.empleado_cargo,
          r.empleado_area,
          r.empleado_email,
          formatDate(r.fecha_evaluacion),
          r.periodo_evaluacion,
          r.estado_evaluacion,
          formatDate(r.fecha_creacion),
          formatDate(r.fecha_autoevaluacion),
          formatDate(r.fecha_evaluacion_jefe),
          formatDate(r.fecha_evaluacion_hseq),
          r.jefe_nombre,
          r.jefe_cargo,
          r.evaluador_hseq_nombre,
          r.evaluador_hseq_cargo,
          r.observaciones_generales,
          r.comentarios_hseq,
          valorONada(r.promedio_autoevaluacion),
          valorONada(r.promedio_evaluacion_jefe),
          valorONada(r.promedio_hseq_detalle),
          valorONada(r.promedio_competencias_detalle),
          valorONada(r.meridian_porcentaje),
          valorONada(r.score_meridian),
          promedioGeneral
        ]);
        // Zebra
        if (idx % 2 === 0) {
          row.eachCell((cell, col) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
          });
        }
        row.eachCell((cell) => {
          cell.border = { bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } } };
          cell.alignment = { vertical: 'middle', wrapText: true };
        });
      });

      // Anchos
      const widths = [14,12,14,28,22,18,26,20,14,16,20,20,20,20,24,18,22,18,36,36,20,20,20,20,14,18,18];
      widths.forEach((w, i) => ws.getColumn(i+1).width = w);

      // Nota con las fórmulas aplicadas
      ws.addRow([]);
      [
        'Cálculo del Promedio General:',
        'NORMAL = Autoevaluación × 20 % + Jefe × 40 % + HSEQ × 40 %.',
        'MERIDIAN = Competencias × 50 % + Meridian × 30 % + HSEQ × 20 %. Meridian: 0-30 % → 1, 31-45 % → 2, 46-60 % → 3, 61-90 % → 4, 91-100 % → 5.',
        'Si falta un componente no se cuenta como 0: su peso se reparte entre los disponibles (suma de valor × peso / suma de pesos presentes).',
        'Todos los componentes corresponden al mismo período de la evaluación.',
      ].forEach((texto, i) => {
        const notaRow = ws.addRow([texto]);
        ws.mergeCells(notaRow.number, 1, notaRow.number, 12);
        notaRow.getCell(1).font = { name: 'Calibri', italic: i > 0, bold: i === 0, color: { argb: 'FF34495E' } };
      });

      // ===== HOJA DE COMPETENCIAS DETALLADAS =====
      wsCompetencias.getCell('A1').value = 'COMPETENCIAS DETALLADAS - MERIDIAN CONSULTING LTDA';
      wsCompetencias.mergeCells('A1:J1');
      const titleComp = wsCompetencias.getCell('A1');
      titleComp.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF1F3B73' } };
      titleComp.alignment = { horizontal: 'center', vertical: 'middle' };

      // Encabezados competencias
      const headersComp = ['ID Evaluación', 'Empleado', 'Aspecto', 'Calificación Empleado', 'Justificación Empleado', 'Calificación Jefe', 'Justificación Jefe', 'Promedio', 'Fecha Creación'];
      const headerRowComp = wsCompetencias.addRow(headersComp);
      headerRowComp.eachCell((cell) => {
        cell.font = { name: 'Calibri', bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = { top:{style:'thin'}, left:{style:'thin'}, right:{style:'thin'}, bottom:{style:'thin'} };
      });

      // Datos competencias
      rows.forEach((r) => {
        if (r.competencias_detalle && Array.isArray(r.competencias_detalle)) {
          r.competencias_detalle.forEach((comp) => {
            const rowComp = wsCompetencias.addRow([
              r.id_evaluacion,
              r.empleado_nombre,
              comp.aspecto,
              comp.calificacion_empleado || '',
              comp.justificacion_empleado || '',
              comp.calificacion_jefe || '',
              comp.justificacion_jefe || '',
              comp.promedio || '',
              formatDate(comp.fecha_creacion)
            ]);
            rowComp.eachCell((cell) => {
              cell.border = { bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } } };
              cell.alignment = { vertical: 'middle', wrapText: true };
            });
          });
        }
      });

      // Anchos competencias
      const widthsComp = [14, 30, 50, 20, 40, 20, 40, 15, 20];
      widthsComp.forEach((w, i) => wsCompetencias.getColumn(i+1).width = w);

      // ===== HOJA DE HSEQ DETALLADO =====
      wsHseq.getCell('A1').value = 'HSEQ DETALLADO - MERIDIAN CONSULTING LTDA';
      wsHseq.mergeCells('A1:K1');
      const titleHseq = wsHseq.getCell('A1');
      titleHseq.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF1F3B73' } };
      titleHseq.alignment = { horizontal: 'center', vertical: 'middle' };

      // Encabezados HSEQ
      const headersHseq = ['ID Evaluación', 'Empleado', 'Responsabilidad', 'Calificación', 'Evaluación Jefe', 'Justificación', 'Fecha Creación Item', 'Fecha Eval. HSEQ', 'Evaluador HSEQ', 'Observaciones', 'Comentarios HSEQ'];
      const headerRowHseq = wsHseq.addRow(headersHseq);
      headerRowHseq.eachCell((cell) => {
        cell.font = { name: 'Calibri', bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = { top:{style:'thin'}, left:{style:'thin'}, right:{style:'thin'}, bottom:{style:'thin'} };
      });

      // Datos HSEQ
      rows.forEach((r) => {
        if (r.hseq_detalle && Array.isArray(r.hseq_detalle)) {
          r.hseq_detalle.forEach((hseq) => {
            const rowHseq = wsHseq.addRow([
              r.id_evaluacion,
              r.empleado_nombre,
              hseq.responsabilidad,
              hseq.calificacion || '',
              hseq.evaluacion_jefe || '',
              hseq.justificacion || '',
              formatDate(hseq.fecha_creacion),
              formatDate(r.fecha_evaluacion_hseq),
              r.evaluador_hseq_nombre || 'Luis Guevara',
              r.observaciones_generales || '',
              r.comentarios_hseq || ''
            ]);
            rowHseq.eachCell((cell) => {
              cell.border = { bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } } };
              cell.alignment = { vertical: 'middle', wrapText: true };
            });
          });
        }
      });

      // Anchos HSEQ
      const widthsHseq = [14, 30, 40, 15, 20, 40, 20, 20, 25, 40, 40];
      widthsHseq.forEach((w, i) => wsHseq.getColumn(i+1).width = w);

      // ===== HOJA DE COMPROMISOS Y SEGUIMIENTO =====
      wsCompromisos.getCell('A1').value = 'COMPROMISOS Y SEGUIMIENTO - MERIDIAN CONSULTING LTDA';
      wsCompromisos.mergeCells('A1:O1');
      const titleCompromisos = wsCompromisos.getCell('A1');
      titleCompromisos.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF1F3B73' } };
      titleCompromisos.alignment = { horizontal: 'center', vertical: 'middle' };

      // Encabezados compromisos y seguimiento
      const headersCompromisos = [
        'ID Evaluación', 'Empleado', 'Período', 'Estado',
        'Fortalezas', 'Aspectos a Mejorar', 'Necesidades Capacitación',
        'Plan Actividad', 'Plan Responsable', 'Plan Seguimiento', 'Plan Fecha',
        'Compromiso Descripción', 'Compromiso Responsable', 'Compromiso Fecha', 'Compromiso Estado'
      ];
      const headerRowCompromisos = wsCompromisos.addRow(headersCompromisos);
      headerRowCompromisos.eachCell((cell) => {
        cell.font = { name: 'Calibri', bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = { top:{style:'thin'}, left:{style:'thin'}, right:{style:'thin'}, bottom:{style:'thin'} };
      });

      // Datos compromisos y seguimiento
      rows.forEach((r, idx) => {
        const mejoramiento = r.mejoramiento || {};
        const planAccion = r.plan_accion || {};
        const actaCompromiso = Array.isArray(r.acta_compromiso) ? r.acta_compromiso : [];
        
        // Si hay acta de compromiso, crear una fila por cada compromiso
        if (actaCompromiso.length > 0) {
          actaCompromiso.forEach((compromiso, compIdx) => {
            const rowCompromiso = wsCompromisos.addRow([
              r.id_evaluacion,
              r.empleado_nombre,
              r.periodo_evaluacion,
              r.estado_evaluacion,
              compIdx === 0 ? (mejoramiento.fortalezas || mejoramiento.fortaleza || '') : '',
              compIdx === 0 ? (mejoramiento.aspectos_mejorar || mejoramiento.aspectosMejorar || '') : '',
              compIdx === 0 ? (mejoramiento.necesidades_capacitacion || mejoramiento.necesidadesCapacitacion || '') : '',
              compIdx === 0 ? (planAccion.actividad || '') : '',
              compIdx === 0 ? (planAccion.responsable || '') : '',
              compIdx === 0 ? (planAccion.seguimiento || '') : '',
              compIdx === 0 ? (planAccion.fecha ? formatDate(planAccion.fecha) : '') : '',
              compromiso.descripcion || compromiso.compromiso || '',
              compromiso.responsable || '',
              compromiso.fecha ? formatDate(compromiso.fecha) : '',
              compromiso.estado || compromiso.cumplido || ''
            ]);
            rowCompromiso.eachCell((cell) => {
              cell.border = { bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } } };
              cell.alignment = { vertical: 'middle', wrapText: true };
            });
            // Zebra
            if ((idx + compIdx) % 2 === 0) {
              rowCompromiso.eachCell((cell) => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
              });
            }
          });
        } else {
          // Si no hay acta de compromiso, crear una fila con mejoramiento y plan de acción
          const rowCompromiso = wsCompromisos.addRow([
            r.id_evaluacion,
            r.empleado_nombre,
            r.periodo_evaluacion,
            r.estado_evaluacion,
            mejoramiento.fortalezas || mejoramiento.fortaleza || '',
            mejoramiento.aspectos_mejorar || mejoramiento.aspectosMejorar || '',
            mejoramiento.necesidades_capacitacion || mejoramiento.necesidadesCapacitacion || '',
            planAccion.actividad || '',
            planAccion.responsable || '',
            planAccion.seguimiento || '',
            planAccion.fecha ? formatDate(planAccion.fecha) : '',
            '',
            '',
            '',
            ''
          ]);
          rowCompromiso.eachCell((cell) => {
            cell.border = { bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } } };
            cell.alignment = { vertical: 'middle', wrapText: true };
          });
          // Zebra
          if (idx % 2 === 0) {
            rowCompromiso.eachCell((cell) => {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
            });
          }
        }
      });

      // Anchos compromisos
      const widthsCompromisos = [14, 30, 14, 16, 40, 40, 30, 40, 25, 40, 20, 50, 25, 20, 15];
      widthsCompromisos.forEach((w, i) => wsCompromisos.getColumn(i+1).width = w);

      const buf = await wb.xlsx.writeBuffer({ useStyles: true, useSharedStrings: true });
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `evaluaciones_completas_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.xlsx`;
      saveAs(blob, fileName);
    } catch (e) {
      alert('Error al generar Excel de evaluaciones: ' + (e?.message || 'desconocido'));
    }
  };

  return (
    <>
      <SEO 
        title="Panel de Administración - Sistema de Evaluación"
        description="Panel de administración del sistema de evaluación de desempeño de Meridian Consulting LTDA. Gestiona empleados, cargos y configuraciones del sistema."
        keywords="panel administración, gestión empleados, gestión cargos, administración sistema, Meridian Consulting, evaluación desempeño"
        url="https://evaluacion.meridianltda.com/admin"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Panel de Administración - Sistema de Evaluación",
          "description": "Panel de administración del sistema de evaluación de desempeño",
          "url": "https://evaluacion.meridianltda.com/admin",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Sistema de Evaluación de Desempeño - Meridian Consulting LTDA",
            "url": "https://evaluacion.meridianltda.com"
          },
          "about": {
            "@type": "Thing",
            "name": "Administración de Sistema de Evaluación"
          }
        }}
      />
      <div className="admin-dashboard">
      <Header onLogout={onLogout} />
      
      <main className="admin-main">
        <div className="admin-container">
          <div className="admin-hero">
            <h1 className="admin-title">Panel de Administrador</h1>
            <p className="admin-subtitle">Seleccione el módulo al que desea acceder</p>
          </div>

          {!coberturaLoading && !coberturaError && cobertura?.resumen && (
            <div
              className={`admin-alerta-strip ${cobertura.resumen.incompletas > 0 ? 'is-warn' : 'is-ok'}`}
              role="status"
            >
              <div className="admin-alerta-strip-text">
                <strong>{cobertura.resumen.incompletas}</strong> incompletas
                · <strong>{cobertura.resumen.completas}</strong> completas
                <span className="admin-alerta-strip-meta">
                  {' '}· Período {cobertura.periodo} · HSEQ {cobertura.anio_hseq}
                  · Sin eval {cobertura.resumen.sin_evaluacion}
                  · Auto {cobertura.resumen.pendiente_auto}
                  · Jefe {cobertura.resumen.pendiente_jefe}
                  · HSEQ {cobertura.resumen.pendiente_hseq}
                </span>
              </div>
              <button
                type="button"
                className="admin-alerta-strip-btn"
                onClick={() => setListaAbierta((v) => !v)}
              >
                {listaAbierta ? 'Ocultar detalle' : 'Ver detalle'}
              </button>
            </div>
          )}
          {coberturaLoading && (
            <p className="admin-cobertura-msg">Cargando avance del período…</p>
          )}
          {coberturaError && (
            <p className="admin-cobertura-msg admin-cobertura-msg-error">{coberturaError}</p>
          )}
          
          <div className="admin-buttons">
            <button 
              className="admin-button empleados"
              onClick={handleEmpleadosClick}
              type="button"
            >
              <div className="button-content">
                <i className="button-icon empleados-icon" aria-hidden="true"></i>
                <div className="button-text">
                  <strong>Gestión de Empleados</strong>
                  <span>Consulta, crea y actualiza la información del personal.</span>
                </div>
              </div>
            </button>
            
            <button 
              className="admin-button cargos"
              onClick={handleCargosClick}
              type="button"
            >
              <div className="button-content">
                <i className="button-icon cargos-icon" aria-hidden="true"></i>
                <div className="button-text">
                  <strong>Gestión de Cargos</strong>
                  <span>Administra los cargos y procesos de la organización.</span>
                </div>
              </div>
            </button>
            
            <button
              className="admin-button competencias"
              onClick={handleCompetenciasClick}
              type="button"
            >
              <div className="button-content">
                <i className="button-icon competencias-icon" aria-hidden="true"></i>
                <div className="button-text">
                  <strong>Banco de Competencias</strong>
                  <span>Configura competencias, comportamientos y fuentes por nivel.</span>
                </div>
              </div>
            </button>

            <button
              className="admin-button periodos"
              onClick={handlePeriodosClick}
              type="button"
            >
              <div className="button-content">
                <i className="button-icon periodos-icon" aria-hidden="true"></i>
                <div className="button-text">
                  <strong>Próximas Evaluaciones</strong>
                  <span>Configura, revisa y activa los períodos de evaluación.</span>
                </div>
              </div>
            </button>

            <button
              className="admin-button reportes"
              onClick={handleDownloadReportClick}
              type="button"
            >
              <div className="button-content">
                <i className="button-icon reportes-icon" aria-hidden="true"></i>
                <div className="button-text">
                  <strong>Descargar Reporte Excel</strong>
                  <span>Exporta el consolidado de evaluaciones del sistema.</span>
                </div>
              </div>
            </button>
          </div>

          {listaAbierta && !coberturaLoading && !coberturaError && cobertura?.resumen && (
            <section className="admin-cobertura" aria-label="Detalle de evaluaciones">
              <div className="admin-cobertura-header">
                <h2 className="admin-cobertura-title">Detalle del período {cobertura.periodo}</h2>
                <p className="admin-cobertura-subtitle">
                  Completa solo cuando están Auto, Jefe y HSEQ. HSEQ corresponde al año {cobertura.anio_hseq}.
                </p>
              </div>

              <div className="admin-cobertura-chips">
                <button type="button" className={`chip ${filtroEtapa === 'INCOMPLETAS' ? 'active' : ''}`} onClick={() => setFiltroEtapa('INCOMPLETAS')}>
                  Incompletas ({cobertura.resumen.incompletas})
                </button>
                <button type="button" className={`chip ${filtroEtapa === 'SIN_EVALUACION' ? 'active' : ''}`} onClick={() => setFiltroEtapa('SIN_EVALUACION')}>
                  Sin evaluación ({cobertura.resumen.sin_evaluacion})
                </button>
                <button type="button" className={`chip ${filtroEtapa === 'PENDIENTE_AUTO' ? 'active' : ''}`} onClick={() => setFiltroEtapa('PENDIENTE_AUTO')}>
                  Pendiente auto ({cobertura.resumen.pendiente_auto})
                </button>
                <button type="button" className={`chip ${filtroEtapa === 'PENDIENTE_JEFE' ? 'active' : ''}`} onClick={() => setFiltroEtapa('PENDIENTE_JEFE')}>
                  Pendiente jefe ({cobertura.resumen.pendiente_jefe})
                </button>
                <button type="button" className={`chip ${filtroEtapa === 'PENDIENTE_HSEQ' ? 'active' : ''}`} onClick={() => setFiltroEtapa('PENDIENTE_HSEQ')}>
                  Pendiente HSEQ ({cobertura.resumen.pendiente_hseq})
                </button>
                <button type="button" className={`chip ${filtroEtapa === 'HSEQ_PENDIENTE' ? 'active' : ''}`} onClick={() => setFiltroEtapa('HSEQ_PENDIENTE')}>
                  Sin HSEQ ({cobertura.resumen.hseq_pendiente})
                </button>
                <button type="button" className={`chip ${filtroEtapa === 'COMPLETAS' ? 'active' : ''}`} onClick={() => setFiltroEtapa('COMPLETAS')}>
                  Completas ({cobertura.resumen.completas})
                </button>
                <button type="button" className={`chip ${filtroEtapa === 'TODAS' ? 'active' : ''}`} onClick={() => setFiltroEtapa('TODAS')}>
                  Todas ({cobertura.resumen.empleados_activos})
                </button>
              </div>

              <div className="admin-cobertura-search">
                <input
                  type="search"
                  placeholder="Buscar por nombre, cédula, cargo, área o jefe"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  aria-label="Buscar empleados en el seguimiento"
                />
                <span className="admin-cobertura-count">{itemsFiltrados.length} resultados</span>
              </div>

              <div className="admin-cobertura-list">
                {itemsFiltrados.map((item) => {
                  const pasos = pasosDeItem(item);
                  return (
                  <article key={item.id_empleado} className={`admin-cobertura-card ${item.completa ? 'is-completa' : 'is-incompleta'}`}>
                    <div className="admin-cobertura-card-main">
                      <h3 className="admin-cobertura-card-name">{item.nombre}</h3>
                      <p className="admin-cobertura-card-meta">
                        {item.cedula}
                        {item.cargo ? ` · ${item.cargo}` : ''}
                        {item.area ? ` · ${item.area}` : ''}
                      </p>
                      <p className="admin-cobertura-card-jefe">
                        Jefe: {item.jefe_nombre || 'Sin asignar'}
                      </p>
                    </div>
                    <div className="admin-cobertura-card-steps" aria-label="Avance de evaluación">
                      <span className={pasoClase(pasos.auto)} title={pasos.auto ? 'Autoevaluación lista' : 'Autoevaluación pendiente'}>Auto</span>
                      <span className={pasoClase(pasos.jefe)} title={pasos.jefe ? 'Evaluación del jefe lista' : 'Evaluación del jefe pendiente'}>Jefe</span>
                      <span className={pasoClase(pasos.hseq)} title={pasos.hseq ? 'HSEQ lista' : 'HSEQ pendiente'}>HSEQ</span>
                    </div>
                    <div className="admin-cobertura-card-status">
                      <span className={`badge-etapa badge-${String(item.etapa).toLowerCase()}`}>
                        {item.etiqueta_etapa}
                      </span>
                      <div className="admin-cobertura-card-pdfs">
                        <button
                          type="button"
                          className="admin-pdf-btn"
                          disabled={!item.id_evaluacion || !pasos.auto}
                          title={item.id_evaluacion ? 'Descargar PDF de la evaluación de desempeño' : 'Sin evaluación en el período'}
                          onClick={() => descargarPdfDesempeno(item)}
                        >
                          PDF Desempeño
                        </button>
                        <button
                          type="button"
                          className="admin-pdf-btn"
                          disabled={!item.tiene_hseq}
                          title={item.tiene_hseq ? 'Descargar PDF de la evaluación HSEQ' : 'HSEQ pendiente'}
                          onClick={() => descargarPdfHseq(item)}
                        >
                          PDF HSEQ
                        </button>
                      </div>
                    </div>
                  </article>
                  );
                })}
                {itemsFiltrados.length === 0 && (
                  <p className="admin-cobertura-empty">No hay registros con este filtro.</p>
                )}
              </div>
            </section>
          )}
          
          <div className="admin-info">
            <p>Desde este panel podrá administrar los datos fundamentales del sistema de evaluación de desempeño.</p>
          </div>
        </div>
      </main>

      <Footer />
      </div>
    </>
  );
}

export default DashboardSelector; 